import api from '../config/api';

class DuffelService {
  constructor() {
    this.baseURL = '/duffel';
  }

  /**
   * Search for flights using Duffel API
   * @param {Object} params - Search parameters
   * @param {string} params.origin - Origin airport code
   * @param {string} params.destination - Destination airport code
   * @param {string} params.departure_date - Departure date (YYYY-MM-DD)
   * @param {string} params.return_date - Return date (YYYY-MM-DD, optional)
   * @param {number} params.passengers - Number of passengers
   * @param {string} params.cabin_class - Cabin class
   * @returns {Promise<Object>} Flight search results
   */
  async searchFlights(params) {
    try {
      console.log('🔍 Duffel frontend search:', params);
      
      // Duffel searches can exceed the default 10s API timeout
      const response = await api.post(`${this.baseURL}/search`, params, { timeout: 25000 });
      
      if (response.data.success) {
        console.log('✅ Duffel search successful:', response.data.flights.length, 'flights found');
        return response.data;
      } else {
        throw new Error(response.data.error || 'Search failed');
      }
    } catch (error) {
      console.error('❌ Duffel search error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Flight search failed');
    }
  }

  /**
   * Search for hotels using Duffel API
   * @param {Object} params - Search parameters
   * @param {string} params.location - Location name
   * @param {string} params.check_in - Check-in date (YYYY-MM-DD)
   * @param {string} params.check_out - Check-out date (YYYY-MM-DD)
   * @param {number} params.guests - Number of guests
   * @returns {Promise<Object>} Hotel search results
   */
  async searchHotels(params) {
    try {
      console.log('🏨 Duffel hotel search:', params);
      
      const response = await api.post(`${this.baseURL}/hotels`, params);
      
      if (response.data.success) {
        console.log('✅ Duffel hotel search successful:', response.data.hotels.length, 'hotels found');
        return response.data;
      } else {
        throw new Error(response.data.error || 'Hotel search failed');
      }
    } catch (error) {
      console.error('❌ Duffel hotel search error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Hotel search failed');
    }
  }

  /**
   * Get detailed stay offer information
   * @param {string} offerId - Stay offer ID
   * @returns {Promise<Object>} Stay offer details
   */
  async getStayOffer(offerId) {
    try {
      const response = await api.get(`${this.baseURL}/stays/offers/${offerId}`);
      
      if (response.data.success) {
        return response.data.offer;
      } else {
        throw new Error(response.data.error || 'Failed to get stay offer');
      }
    } catch (error) {
      console.error('❌ Duffel get stay offer error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to get stay offer details');
    }
  }

  /**
   * Create a stay booking
   * @param {string} offerId - Stay offer ID to book
   * @param {Object} guestInfo - Guest information
   * @returns {Promise<Object>} Stay booking confirmation
   */
  async createStayBooking(offerId, guestInfo) {
    try {
      console.log('📝 Creating stay booking for offer:', offerId);
      
      const response = await api.post(`${this.baseURL}/stays/bookings`, {
        offerId,
        guestInfo
      });
      
      if (response.data.success) {
        console.log('✅ Stay booking created:', response.data.booking.id);
        return response.data.booking;
      } else {
        throw new Error(response.data.error || 'Stay booking failed');
      }
    } catch (error) {
      console.error('❌ Duffel stay booking error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Stay booking failed');
    }
  }

  /**
   * Cancel a stay booking
   * @param {string} bookingId - Stay booking ID to cancel
   * @returns {Promise<Object>} Cancellation confirmation
   */
  async cancelStayBooking(bookingId) {
    try {
      console.log('❌ Cancelling stay booking:', bookingId);
      
      const response = await api.post(`${this.baseURL}/stays/bookings/${bookingId}/cancel`);
      
      if (response.data.success) {
        console.log('✅ Stay booking cancelled:', response.data.cancellation.id);
        return response.data.cancellation;
      } else {
        throw new Error(response.data.error || 'Stay cancellation failed');
      }
    } catch (error) {
      console.error('❌ Duffel stay cancellation error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Stay cancellation failed');
    }
  }

  /**
   * Get detailed offer information
   * @param {string} offerId - Offer ID
   * @returns {Promise<Object>} Offer details
   */
  async getOffer(offerId) {
    try {
      const response = await api.get(`${this.baseURL}/offers/${offerId}`);
      
      if (response.data.success) {
        return response.data.offer;
      } else {
        throw new Error(response.data.error || 'Failed to get offer');
      }
    } catch (error) {
      console.error('❌ Duffel get offer error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to get offer details');
    }
  }

  /**
   * Create a booking
   * @param {string} offerId - Offer ID to book
   * @param {Array} passengers - Passenger information
   * @returns {Promise<Object>} Booking confirmation
   */
  async createBooking(offerId, passengers) {
    try {
      console.log('📝 Creating Duffel booking:', { offerId, passengerCount: passengers.length });
      
      const response = await api.post(`${this.baseURL}/booking`, {
        offerId,
        passengers
      });
      
      if (response.data.success) {
        console.log('✅ Duffel booking created:', response.data.booking.id);
        return response.data.booking;
      } else {
        throw new Error(response.data.error || 'Booking failed');
      }
    } catch (error) {
      console.error('❌ Duffel booking error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Booking creation failed');
    }
  }

  /**
   * Get booking details
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object>} Booking details
   */
  async getBooking(bookingId) {
    try {
      const response = await api.get(`${this.baseURL}/booking/${bookingId}`);
      
      if (response.data.success) {
        return response.data.booking;
      } else {
        throw new Error(response.data.error || 'Failed to get booking');
      }
    } catch (error) {
      console.error('❌ Duffel get booking error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to get booking details');
    }
  }

  /**
   * Cancel a booking
   * @param {string} bookingId - Booking ID to cancel
   * @returns {Promise<Object>} Cancellation confirmation
   */
  async cancelBooking(bookingId) {
    try {
      const response = await api.post(`${this.baseURL}/booking/${bookingId}/cancel`);
      
      if (response.data.success) {
        return response.data.cancellation;
      } else {
        throw new Error(response.data.error || 'Cancellation failed');
      }
    } catch (error) {
      console.error('❌ Duffel cancel booking error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Booking cancellation failed');
    }
  }

  /**
   * Get airport information
   * @param {string} iataCode - Airport IATA code
   * @returns {Promise<Object>} Airport information
   */
  async getAirportInfo(iataCode) {
    try {
      const response = await api.get(`${this.baseURL}/airports/${iataCode}`);
      
      if (response.data.success) {
        return response.data.airport;
      } else {
        throw new Error(response.data.error || 'Failed to get airport info');
      }
    } catch (error) {
      console.error('❌ Duffel get airport error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to get airport information');
    }
  }

  /**
   * Check Duffel service health
   * @returns {Promise<Object>} Service status
   */
  async checkHealth() {
    try {
      const response = await api.get(`${this.baseURL}/health`);
      return response.data;
    } catch (error) {
      console.error('❌ Duffel health check error:', error);
      return {
        success: false,
        status: 'disconnected',
        error: error.message
      };
    }
  }

  /**
   * Format flight data for display
   * @param {Object} flight - Raw flight data
   * @returns {Object} Formatted flight data
   */
  formatFlightForDisplay(flight) {
    return {
      id: flight.id,
      airline: flight.airline,
      departure: {
        airport: flight.departure.airport,
        city: flight.departure.city,
        time: this.formatTime(flight.departure.time),
        terminal: flight.departure.terminal
      },
      arrival: {
        airport: flight.arrival.airport,
        city: flight.arrival.city,
        time: this.formatTime(flight.arrival.time),
        terminal: flight.arrival.terminal
      },
      duration: flight.duration,
      stops: flight.stops,
      price: flight.price,
      cabin_class: flight.cabin_class,
      return_flight: flight.return_flight,
      booking_url: flight.booking_url
    };
  }

  /**
   * Format time for display
   * @param {string} isoTime - ISO time string
   * @returns {string} Formatted time
   */
  formatTime(isoTime) {
    if (!isoTime) return 'Unknown';
    
    const date = new Date(isoTime);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  /**
   * Format price for display
   * @param {Object} price - Price object
   * @returns {string} Formatted price
   */
  formatPrice(price) {
    if (!price) return 'Price not available';
    
    const amount = parseFloat(price.total);
    const currency = price.currency || 'USD';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Validate passenger data
   * @param {Array} passengers - Passenger data
   * @returns {Object} Validation result
   */
  validatePassengers(passengers) {
    const errors = [];
    
    if (!Array.isArray(passengers) || passengers.length === 0) {
      errors.push('At least one passenger is required');
      return { valid: false, errors };
    }

    const requiredFields = ['firstName', 'lastName', 'email', 'dateOfBirth'];
    
    passengers.forEach((passenger, index) => {
      requiredFields.forEach(field => {
        if (!passenger[field]) {
          errors.push(`Passenger ${index + 1}: ${field} is required`);
        }
      });
      
      // Validate email format
      if (passenger.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(passenger.email)) {
        errors.push(`Passenger ${index + 1}: Invalid email format`);
      }
      
      // Validate date of birth
      if (passenger.dateOfBirth) {
        const dob = new Date(passenger.dateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear();
        
        if (isNaN(dob.getTime())) {
          errors.push(`Passenger ${index + 1}: Invalid date of birth format`);
        } else if (age < 0 || age > 120) {
          errors.push(`Passenger ${index + 1}: Invalid age`);
        }
      }
    });

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

const duffelService = new DuffelService();
export default duffelService;
